import React, { Suspense, lazy, useMemo, useState, useEffect } from 'react';
import {
  StyledFooter,
  StyledFooterSpacer,
  StyledHorizontalBar,
  koreaLegalFooterDivider,
  minimalFooterClass,
} from './styles';
import FooterMenu from '../FooterMenu';
import SubFooter from '../SubFooter';
import { GLOBAL_NAV_KOREAN_FOOTER, MINIMAL_FOOTER_EXPERIENCES } from '../../constants';
import KoreanLegalFooter from '../KoreanLegalFooter';
import useFeatureFlags from '../../utils/hooks/featureFlagsHook';
import { LanguageMenuContent } from '../LanguageMenu';
import { useNavOptionsContext } from '../../context/optionsContext';
import useExperiment, { getExperiment } from '../../utils/hooks/experimentHook';
import { NikeAIVariantProvider } from '../../context/nikeAIVariantContext';

// Lazy load Nike AI variant components for code splitting
const NikeAIVariantA = lazy(() => import('../NikeAIVariantA'));
const NikeAIVariantB = lazy(() => import('../NikeAIVariantB'));

const Footer = () => {
  const isKorea = useFeatureFlags(GLOBAL_NAV_KOREAN_FOOTER);
  const { opts } = useNavOptionsContext();
  const minimalFooterEnabled = MINIMAL_FOOTER_EXPERIENCES.includes(opts?.appInfo?.viewChannel);

  // Only render Nike AI on client-side (after hydration) to avoid SSR Suspense issues
  const [isClient, setIsClient] = useState(false);

  useEffect(() => {
    setIsClient(true);
  }, []);

  // Fetch Nike AI experiment data (client-side only)
  // 📝 ALTERNATIVE WITHOUT CONTEXT: This experiment fetch would remain the same
  const experimentData = useExperiment([{ name: 'search_digital_coach' }]);

  // Determine which Nike AI variant to render
  // 📝 ALTERNATIVE WITHOUT CONTEXT: variant would not be needed for context, only component
  const { variant, component } = useMemo(() => {
    // Don't render during SSR
    if (!isClient) {
      return { variant: null, component: null };
    }

    if (!experimentData) {
      // Experiment not loaded yet - show nothing while loading
      return { variant: null, component: null };
    }

    // Get the specific experiment
    const experiment = getExperiment(experimentData, 'search_digital_coach');

    if (!experiment || !experiment.content) {
      // No experiment found or no content - true control (no Nike AI)
      return { variant: null, component: null };
    }

    // Use variant name directly from optimization platform
    const variant = experiment.content.variant;

    if (variant === 'variant-A') {
      return { variant, component: <NikeAIVariantA /> };
    }

    if (variant === 'variant-B') {
      return { variant, component: <NikeAIVariantB /> };
    }

    // Unknown variant - default to no Nike AI (control)
    return { variant: null, component: null };
  }, [isClient, experimentData]);

  return (
    // 📝 ALTERNATIVE WITHOUT CONTEXT: Remove this Provider wrapper entirely
    <NikeAIVariantProvider variant={variant}>
      <StyledFooter className={minimalFooterEnabled && minimalFooterClass} role="contentinfo">
        <StyledHorizontalBar />
        <FooterMenu />
        <StyledFooterSpacer className="footer-spacer" />
        <SubFooter />
        {isKorea && (
          <>
            <StyledHorizontalBar className={koreaLegalFooterDivider} />
            <KoreanLegalFooter />
          </>
        )}
        {/* adding this here so the lang menu links are rendered for SEO purposes */}
        <LanguageMenuContent />
      </StyledFooter>

      {/* Nike AI variant loader - client-side only with lazy loading */}
      {isClient && <Suspense fallback={null}>{component}</Suspense>}
    </NikeAIVariantProvider>
  );
};

export default Footer;
